import { ArrowLeft, Calendar } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const PRALScoreDetail = () => {
  const navigate = useNavigate();

  const dailyData = Array.from({ length: 7 }, (_, i) => ({
    meal: `Meal ${i + 1}`,
    score: Math.floor(Math.random() * 20 - 15),
  }));

  const weeklyData = [
    { day: "Mon", score: -10.2 },
    { day: "Tue", score: -8.5 },
    { day: "Wed", score: -14.8 },
    { day: "Thu", score: -11.3 },
    { day: "Fri", score: -9.7 },
    { day: "Sat", score: -13.2 },
    { day: "Sun", score: -12.5 },
  ];

  const monthlyData = [
    { week: "Week 1", score: -10.2 },
    { week: "Week 2", score: -11.5 },
    { week: "Week 3", score: -13.1 },
    { week: "Week 4", score: -12.5 },
  ];

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">PRAL Score</h1>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl"
            onClick={() => {
              // TODO: Implement calendar functionality
            }}
          >
            <Calendar className="h-5 w-5" />
          </Button>
        </div>

        {/* Today's Stats */}
        <Card className="from-success/10 to-success/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's PRAL Score
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">-12.5</div>
            <p className="text-sm text-muted-foreground">mEq/day (Alkaline)</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">Acidic</span>
              <div className="via-warning to-success h-2 w-48 rounded-full bg-gradient-to-r from-destructive">
                <div className="relative h-full w-full">
                  <div className="absolute left-[70%] top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-background bg-foreground" />
                </div>
              </div>
              <span className="text-xs text-muted-foreground">Alkaline</span>
            </div>
          </div>
        </Card>

        {/* What is PRAL Score */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is PRAL Score?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            PRAL (Potential Renal Acid Load) measures the acidity or alkalinity
            of your diet. Negative values indicate an alkaline-forming diet,
            which is generally associated with better bone health, reduced
            inflammation, and improved gut health. Most vegetables and fruits
            create negative PRAL scores, while meats and processed foods create
            positive scores.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Highly Alkaline</span>
              <span className="text-success font-semibold">-20 to -10</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderately Alkaline</span>
              <span className="font-semibold">-10 to -5</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Neutral</span>
              <span className="font-semibold">-5 to +5</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Acidic</span>
              <span className="font-semibold text-destructive">+5 to +20</span>
            </div>
          </div>
        </Card>

        {/* Tabs for Daily/Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-3 p-1">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="meal"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="score"
                    fill="hsl(var(--primary))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--primary))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="score"
                    fill="hsl(var(--primary))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Improvement Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Foods to Increase Alkalinity
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">•</span>
              <span>Leafy greens (spinach, kale, arugula)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">•</span>
              <span>Fruits (especially citrus, melons, and berries)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">•</span>
              <span>Root vegetables (carrots, beets, sweet potatoes)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-success mt-0.5">•</span>
              <span>Legumes and beans</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default PRALScoreDetail;
